import { forwardRef, useCallback, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Fade, Typography, Box, OutlinedInput, Link } from '@mui/material';
import * as Yup from 'yup';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import { Form, FormikProvider, useFormik } from 'formik';
import useSecondCountdown from 'src/hooks/useSecondCountdown';

interface DeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onVerifyCode: (code: string) => void;
  onReSendCode: (phoneNumber?: string) => void;
}

type InitialValues = {
  code1: string;
  code2: string;
  code3: string;
  code4: string;
  code5: string;
};

type ValueNames = 'code1' | 'code2' | 'code3' | 'code4' | 'code5';

const DeleteDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogTitle-root': {
    color: theme.palette.text2.black,
    height: 35
  },
  '& .MuiDialogActions-root': {
    paddingRight: 22,
    paddingBottom: 30
  },
  '& .MuiDialogContent-root': {
    paddingTop: '40px !important',
  },
}));

// eslint-disable-next-line consistent-return
const maxLength = (event: any) => {
  if (event.target.value.length >= event.target.getAttribute('maxlength')) {
    event.currentTarget.nextElementSibling?.children[0].focus();
  }
  if (event.target.value.length > event.target.maxLength) {
    return (event.target.value = event.target.value.slice(
      0,
      event.target.maxLength,
    ));
  }
};

const handleProcess =
  (isSubmitting: boolean, index: number, handleSubmit: any) => (event: any) => {
    // TODO: add if the key code in number or is Enter do the handleSubmit.
    if (!isSubmitting && index === 5 && event.currentTarget.value.length > 0) {
      handleSubmit();
    }
    if (event.keyCode === 46 || event.keyCode === 8) {
      event.currentTarget.parentNode.previousElementSibling?.children[0].focus();
    }
  };

const Transition = forwardRef((
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) => (
  <Fade
    ref={ref}
    {...props}
  />
));

const ProfileChangeNumberDialog =
({ open, onClose, onVerifyCode, onReSendCode }: DeleteDialogProps) => {
  const VerifyCodeSchema = Yup.object().shape({
    code1: Yup.number().required('کد الزامی می‌باشد'),
    code2: Yup.number().required('کد الزامی می‌باشد'),
    code3: Yup.number().required('کد الزامی می‌باشد'),
    code4: Yup.number().required('کد الزامی می‌باشد'),
    code5: Yup.number().required('کد الزامی می‌باشد'),
  });

  const formik = useFormik<InitialValues>({
    initialValues: {
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
    },
    validationSchema: VerifyCodeSchema,
    onSubmit: async (values) => {
      const code = `${values.code1}${values.code2}${values.code3}${values.code4}${values.code5}`;
      onVerifyCode(code);
    },
  });

  const { errors, values, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const { code1, code2, code3, code4, code5 } = values;
  const disabled = !code1 || !code2 || !code3 || !code4 || !code5;

  const { countdown, restart, start } = useSecondCountdown({ init: 60, automatic: false });
  const resendAllowed = Boolean(countdown === 0);

  const reSendCode = useCallback(() => {
    restart();
    onReSendCode();
  }, []);

  useEffect(() => {
    start();
  }, [open]);

  return (
    <DeleteDialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
      TransitionComponent={Transition}
    >
      <FormikProvider value={formik}>
        <Form
          autoComplete="off"
          noValidate
          style={{ width: '100%' }}
          onSubmit={handleSubmit}
        >
          <DialogTitle>
            کد تایید خود را وارد کنید
          </DialogTitle>
          <DialogContent>
            <Box
              flexDirection="row-reverse"
              sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}
            >
              {Object.keys(values).map((item, i) => (
                <OutlinedInput
                  autoFocus={i === 0}
                  style={{ direction: 'ltr', marginLeft: 20 }}
                  key={item}
                  {...getFieldProps(item)}
                  onFocus={(e) => {
                    e.target.select();
                    e.target.placeholder = '';
                  }}
                  onBlur={(e) => {
                    e.target.placeholder = '';
                  }}
                  onKeyUp={handleProcess(isSubmitting, i, handleSubmit)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                  onInput={maxLength}
                  error={Boolean(touched[item as ValueNames] && errors[item as ValueNames])}
                  inputProps={{
                    maxLength: 1,
                    sx: {
                      p: 0,
                      textAlign: 'center',
                      width: { xs: 36, sm: 56 },
                      height: { xs: 36, sm: 56 },
                    },
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography>
                کد تایید مجدد برای شما ارسال خواهد شد.
              </Typography>
              <Link
                disabled={!resendAllowed}
                sx={{
                  color: resendAllowed ? '' : 'text.secondary',
                  cursor: resendAllowed ? 'pointer' : 'default',
                }}
                onClick={reSendCode}
                variant="button"
                component="button"
                underline={resendAllowed ? 'always' : 'none'}
              >
                {resendAllowed ? 'ارسال مجدد کد' : <span>&nbsp; {countdown}&nbsp;ثانیه</span>}
              </Link>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              fullWidth
              size="large"
              color="primary"
              variant="outlined"
              sx={{ width: 100, borderRadius: 25 }}
              onClick={onClose}
            >
              انصراف
            </Button>
            <Button
              fullWidth
              size="large"
              color="primary"
              variant="contained"
              disabled={disabled || isSubmitting}
              sx={{ width: 100, borderRadius: 25 }}
              type="submit"
            >
              تایید
            </Button>
          </DialogActions>
        </Form>
      </FormikProvider>
    </DeleteDialog>
  );
};

export default ProfileChangeNumberDialog;
